import { ProcessingOverlay } from '@components'

import { BorderRadius, Color, FormItemsGroup, Spacing, Text } from '@billy/design-system'
import { ReactElement } from 'react'
import { useTranslation } from 'react-i18next'

import { useInvoiceComplianceMode } from '@features/invoices/hooks/useInvoiceComplianceMode'

import { getIsAddressExemptionAvailable } from '../../../../utils/getIsAddressExemptionAvailable'
import { getIsSubmitActionTypeCreate } from '../../../../utils/getIsSubmitActionTypeCreate'
import { useCustomerForm } from '../../contexts/customerFormContext'
import { Address } from '../Address'
import { AddressExemption } from '../AddressExemption'
import { ContactInfo } from '../ContactInfo'
import { ContactPersons } from '../ContactPersons'
import { CustomerTypeToggle } from '../CustomerTypeToggle'
import { OptionalItems } from '../OptionalItems'
import { ShippingAddress } from '../ShippingAddress'

export const IndividualCustomerModalBody = (): ReactElement => {
  const { t } = useTranslation()
  const { customerForm, customerType, submitActionType, isLoading, isSubmitting } = useCustomerForm()
  const { isFrenchComplianceMode } = useInvoiceComplianceMode()

  const { watch } = customerForm
  const [customerCountry, isAddressExempt] = watch(['address.country', 'isAddressExempt'])
  const shouldShowCustomerTypeToggle = getIsSubmitActionTypeCreate(submitActionType)
  const shouldShowProcessingOverlay = isLoading || isSubmitting
  const shouldShowAddressExemption = getIsAddressExemptionAvailable({
    customerCountry,
    customerType,
    isFrenchComplianceMode,
  })
  const isAddressDisabled = shouldShowAddressExemption && !!isAddressExempt

  return (
    <>
      {shouldShowProcessingOverlay && (
        <ProcessingOverlay borderRadius={BorderRadius.R12} color={Color.White} withAnimation />
      )}
      <FormItemsGroup rowSpacing={Spacing.S12}>
        {shouldShowCustomerTypeToggle && <CustomerTypeToggle />}
        <ContactInfo />
        <ContactPersons />
        <Text variant="h3"> {t('customer_modal.form.individual_address.header')}</Text>
        <Address isDisabled={isAddressDisabled} />
        {shouldShowAddressExemption && <AddressExemption />}
        {!isAddressDisabled && <ShippingAddress />}
        <OptionalItems />
      </FormItemsGroup>
    </>
  )
}
